import { useState } from "react";

type FeedbackValues = {
  name: string;
  email: string;
  type: string;
  film: string;
  message: string;
};

const initialValues: FeedbackValues = {
  name: "",
  email: "",
  type: "资料勘误",
  film: "",
  message: "",
};

type Status = "idle" | "submitting" | "success" | "error";

const FeedbackForm = () => {
  const [values, setValues] = useState<FeedbackValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const update = (key: keyof FeedbackValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = (data: FeedbackValues) => {
    const next: Record<string, string> = {};
    if (!data.name.trim()) next.name = "请填写你的称呼";
    if (!data.email.trim()) next.email = "请填写邮箱，便于我们回复";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) next.email = "邮箱格式看起来不太对";
    if (!data.message.trim()) next.message = "请简单描述你的反馈内容";
    else if (data.message.trim().length > 500) next.message = "反馈内容请控制在 500 字以内";
    return next;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedbackMessage("感谢你的反馈，我们会尽快核对并回复。");
      setValues(initialValues);
      return;
    }

    const payload: FeedbackValues = {
      name: String(formData.get("name") ?? ""),
      email: String(formData.get("email") ?? ""),
      type: String(formData.get("type") ?? ""),
      film: String(formData.get("film") ?? ""),
      message: String(formData.get("message") ?? ""),
    };

    const validation = validate(payload);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      setStatus("error");
      setFeedbackMessage("表单还有未填写或格式不正确的项目，请检查后重试。");
      return;
    }

    setStatus("submitting");
    setFeedbackMessage("");

    const body = new URLSearchParams();
    body.append("name", payload.name);
    body.append("email", payload.email);
    body.append("type", payload.type);
    if (payload.film.trim()) body.append("film", payload.film);
    body.append("message", payload.message);

    try {
      const response = await fetch("https://readdy.ai/api/form/dbae28sdsci36477l6tg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFeedbackMessage(serverMsg ? `提交未成功：${serverMsg}` : "提交未成功，请稍后再试。");
        return;
      }

      setStatus("success");
      setFeedbackMessage("感谢你的反馈，我们已经收到，会在 3 个工作日内通过邮件回复。");
      setValues(initialValues);
      form.reset();
    } catch {
      setStatus("error");
      setFeedbackMessage("网络出现波动，反馈没有发送成功，请稍后重试。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7"
      noValidate
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950">资料勘误与反馈</h2>
      <p className="mt-1.5 text-sm text-foreground-500">
        发现资料有误、希望补充条目，或想推荐一部经典作品？留言给我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="feedback-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
            你的称呼 <span className="text-accent-600">*</span>
          </label>
          <input
            id="feedback-name"
            name="name"
            type="text"
            value={values.name}
            onChange={(event) => update("name", event.target.value)}
            placeholder="例如：影迷小林"
            className={inputClass}
          />
          {errors.name ? <p className="mt-1 text-xs text-accent-700">{errors.name}</p> : null}
        </div>
        <div>
          <label htmlFor="feedback-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="feedback-email"
            name="email"
            type="email"
            value={values.email}
            onChange={(event) => update("email", event.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
          {errors.email ? <p className="mt-1 text-xs text-accent-700">{errors.email}</p> : null}
        </div>
        <div>
          <label htmlFor="feedback-type" className="mb-1.5 block text-xs font-medium text-foreground-700">
            反馈类型
          </label>
          <div className="relative">
            <select
              id="feedback-type"
              name="type"
              value={values.type}
              onChange={(event) => update("type", event.target.value)}
              className={`${inputClass} cursor-pointer appearance-none pr-9`}
            >
              <option value="资料勘误">资料勘误</option>
              <option value="作品推荐">作品推荐</option>
              <option value="功能建议">功能建议</option>
              <option value="合作联系">合作联系</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
        </div>
        <div>
          <label htmlFor="feedback-film" className="mb-1.5 block text-xs font-medium text-foreground-700">
            相关作品（选填）
          </label>
          <input
            id="feedback-film"
            name="film"
            type="text"
            value={values.film}
            onChange={(event) => update("film", event.target.value)}
            placeholder="例如：色即是空"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="feedback-message" className="text-xs font-medium text-foreground-700">
            反馈内容 <span className="text-accent-600">*</span>
          </label>
          <span className="text-[11px] text-foreground-400">{values.message.length} / 500</span>
        </div>
        <textarea
          id="feedback-message"
          name="message"
          rows={5}
          maxLength={500}
          value={values.message}
          onChange={(event) => update("message", event.target.value.slice(0, 500))}
          placeholder="请描述你发现的问题或建议，越具体越好，最多 500 字。"
          className={`${inputClass} resize-none leading-relaxed`}
        />
        {errors.message ? <p className="mt-1 text-xs text-accent-700">{errors.message}</p> : null}
      </div>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-optional-field"
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] leading-relaxed text-foreground-400">
          提交即表示同意我们通过邮件与你就该反馈进行联系，不会用于其他用途。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-wait disabled:opacity-70"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          </span>
          {status === "submitting" ? "正在提交" : "提交反馈"}
        </button>
      </div>

      {status === "success" ? (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-700">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-checkbox-circle-fill" />
          </span>
          {feedbackMessage}
        </p>
      ) : null}
      {status === "error" && feedbackMessage ? (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-3.5 py-3 text-sm text-accent-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-fill" />
          </span>
          {feedbackMessage}
        </p>
      ) : null}
    </form>
  );
};

export default FeedbackForm;